import { useContext, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import AddToArtlistIcon from '@/assets/all-icons/add-to-artlist-icon';
import DownloadTemplateIcon from '@/assets/all-icons/download-template-icon';
import EditArtworkIcon from '@/assets/all-icons/edit-artwork-icon';
import ShareIcon from '@/assets/all-icons/share-icon';
import UserIcon from '@/assets/all-icons/user-icon';
import ArtworkDataContext from '@/context/ArtworkDataContext';
import useProtectAction from '@/hooks/useProtectAction';
import useStateHandler from '@/hooks/useStateHandler';
import PopupUpdateProfilePicture from '@/pages/artwork/artwork-header/options/download-template/components/PopupUpdateProfilePicture';
import ThreeDotsHorizontal from '@/shared-components/icons/components/public/styles/ThreeDotsHorizontal';
import XIcon from '@/shared-components/icons/components/public/XIcon';
import GenericPopup from '@/shared-components/popups/components/GenericPopup';

import OptionItem from './components/ArtworkViewOptions';
import SaveArtwork from './components/SaveArtwork';
import AddToArtlist from './add-to-artlist';
import DowloadTemplate from './download-template';

import './index.css';

export default function Options({ open, close }) {
  const { artworkData } = useContext(ArtworkDataContext);
  const { state } = useStateHandler();
  const { pathname } = useLocation();
  const [openAddToArtlist, setOpenAddToArtlist] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDownloadTemplate, setOpenDownloadTemplate] = useState(false);
  const [openCropPopup, setOpenCropPopup] = useState(false); // State for crop popup
  const privateActionHandler = useProtectAction({ typeOfProtection: "USER_ACCOUNT_REQUIRED" });
  const navigate = useNavigate();

  function shareArtwork() {
    navigator.share({
      title: `${artworkData.artwork_about.artwork_title}`,
      text: `Take a look at ${artworkData.artwork_about.artwork_title}:`,
      url: `https://suarte.art/artwork/${artworkData._id}`
    });
  }

  function handleEditArtwork() {
    if (artworkData.artwork_status === "original_sold" || artworkData.artwork_status === "sold") {
      setOpenEditDialog(true);
    } else {
      navigate("/profile/edit-artwork", { state: { from: true, artworkData } });
    }
  }

  function isArtworkGallery() {
    return state.user_session?._id === artworkData.artwork_about.artwork_gallery._id;
  }

  // New function to handle setting the artwork as the profile picture
  function handleSetAsProfilePicture() {
    setOpenCropPopup(true); // Open the crop popup for profile picture selection
  }

  return (
    <>
      <GenericPopup
        open={open}
        className="artwork-options__container"
        animation={false}
        onTouchStart={e => e.stopPropagation()}
        onTouchEnd={e => e.stopPropagation()}
        onWheel={e => e.stopPropagation()}
        onClick={close}
      >
        <div className="artwork-options__background" />

        <div className="artwork-options__wrapper element-non-selectable">
          <div className="artwork-view-options-container">
            <ThreeDotsHorizontal className={pathname.includes("suarteworld") ? "artwork-options__fake-dots suarteworld" : "artwork-options__fake-dots"} />

            <div className="artwork-options__options">

              {(isArtworkGallery() && artworkData.artwork_status !== "unavailable") && (
                <>
                  <OptionItem
                    text="Edit artwork"
                    icon={<EditArtworkIcon className="edit-artwork-icon" />}
                    onClick={handleEditArtwork}
                  />

                  <OptionItem
                    text="Download template"
                    icon={<DownloadTemplateIcon className="download-template-icon" />}
                    onClick={() => privateActionHandler(() => setOpenDownloadTemplate(true))}
                  />

                </>
              )}

              <OptionItem
                text="Add to artlist"
                icon={<AddToArtlistIcon className="add-to-artlist-icon" />}
                onClick={() => privateActionHandler(() => setOpenAddToArtlist(true))}
              />

              <SaveArtwork />
              
              {/* New "Set as profile picture" option */}
              <OptionItem
                text="Set as profile picture"
                icon={<UserIcon className="user-icon" />} // Use an appropriate icon
                onClick={handleSetAsProfilePicture}
              />

              {navigator.share && (
                <OptionItem
                  text="Share"
                  icon={<ShareIcon className="share-icon" />}
                  onClick={shareArtwork}
                />
              )}
            </div>
          </div>
        </div>
      </GenericPopup>

      <AddToArtlist open={openAddToArtlist} close={() => setOpenAddToArtlist(false)} />
      <DowloadTemplate open={openDownloadTemplate} close={()=>setOpenDownloadTemplate(false)} />

      {/* Crop popup for setting profile picture */}
      <PopupUpdateProfilePicture
        open={openCropPopup}
        image={`https://imagedelivery.net/hAeIC__6Aj746x0RFU1joA/${artworkData.artwork_media.artwork_main_picture.image_id}/public`}
        exitCrop={() => setOpenCropPopup(false)}
      />

      <GenericPopup
        open={openEditDialog}
        className="artwork-options__edit-dialog"
        opacity
        onTouchStart={e => e.stopPropagation()}
        onTouchEnd={e => e.stopPropagation()}
        onWheel={e => e.stopPropagation()}
      >
        <div className="generic-popup-header">
          <span className="generic-popup-header-text font-size-medium">
            The work cannot be edited
          </span>

          <XIcon className="generic-popup-close" onClick={() => setOpenEditDialog(false)} />
        </div>

        <p className="artwork-options__edit-dialog-text font-size-medium">
          Since this artwork has already been sold, making changes is no longer possible.
          If you believe that further modifications are necessary, please feel free to
          reach out to us via email at {" "}
          <a href="mailto: contact@suarte.art">contact@suarte.art</a>. We appreciate
          your cooperation.
        </p>
      </GenericPopup>
    </>
  );

}
